/**
 * IX3TransformEngine - Transformation engine for IX3 data
 */

import {produce, Draft} from 'immer';

import {
  Interaction,
  Timeline,
  TimelineAction,
  Trigger,
  Condition,
  TargetDefinition,
  ExtensionKey,
  TriggerConfig,
  AnimationConfigValues,
  TriggerConditionalLogic,
  TimelineControlType,
} from '@packages/systems/ix3/core';

import type {
  TransformBaseContext,
  IX3TransformPlugin,
  ActionTransformFunction,
  TriggerTransformFunction,
  ConditionTransformFunction,
  TargetTransformFunction,
} from './types';

export interface TransformResult<T> {
  data: T;
  errors: TransformError[];
}

/**
 * Unified transformation engine that handles both designer and publish contexts
 * Provides a single, cohesive system for all IX3 data transformations
 */
export class IX3TransformEngine<TContext extends Record<string, unknown>> {
  private triggerTransforms = new Map<
    ExtensionKey,
    TriggerTransformFunction<TContext>
  >();
  private targetTransforms = new Map<
    ExtensionKey,
    TargetTransformFunction<TContext>
  >();
  private conditionTransforms = new Map<
    ExtensionKey,
    ConditionTransformFunction<TContext>
  >();
  private actionTransforms = new Map<
    ExtensionKey,
    ActionTransformFunction<TContext>
  >();
  private pathTracker = new PathTracker();
  private errorCollector = new ErrorCollector();

  constructor(private config: TransformEngineConfig) {}

  /**
   * Main transformation entry point
   * @param data - IX3 data to transform
   * @param context - Transformation context (designer or publish)
   * @returns Transformed data with any errors encountered
   */
  public transform(
    data: {ix3Interactions?: Interaction[]; ix3Timelines?: Timeline[]},
    context: TransformBaseContext<TContext>
  ): TransformResult<{
    ix3Interactions?: Interaction[];
    ix3Timelines?: Timeline[];
  }> {
    // Reset state for new transformation
    this.pathTracker.reset();
    this.errorCollector.clear();

    const transformedData = produce(data, (draft) => {
      this.transformIX3Data(draft, context);
    });

    return {
      data: transformedData,
      errors: this.errorCollector.getErrors(),
    };
  }

  /**
   * Registers a plugin with the engine
   * @param plugin - Plugin to register
   */
  public registerPlugin(plugin: IX3TransformPlugin<TContext>): void {
    for (const [extensionKey, transform] of plugin.triggerTransforms) {
      this.triggerTransforms.set(extensionKey, transform);
    }
    for (const [extensionKey, transform] of plugin.targetTransforms) {
      this.targetTransforms.set(extensionKey, transform);
    }
    for (const [extensionKey, transform] of plugin.conditionTransforms) {
      this.conditionTransforms.set(extensionKey, transform);
    }
    for (const [extensionKey, transform] of plugin.actionTransforms) {
      this.actionTransforms.set(extensionKey, transform);
    }
  }

  /** Clears all registered transforms */
  clear(): void {
    this.triggerTransforms.clear();
    this.targetTransforms.clear();
    this.conditionTransforms.clear();
    this.actionTransforms.clear();
  }

  // Context creation helper methods

  private createActionTransformContext(
    baseContext: TransformBaseContext<TContext>,
    timelineAction: TimelineAction,
    timeline: Timeline
  ) {
    return {
      ...baseContext,
      timelineAction,
      timeline,
    };
  }

  private createTriggerTransformContext(
    baseContext: TransformBaseContext<TContext>,
    trigger: Trigger,
    interaction: Interaction
  ) {
    return {
      ...baseContext,
      trigger,
      interaction,
    };
  }

  private createConditionTransformContext(
    baseContext: TransformBaseContext<TContext>,
    triggerConditionalLogic: TriggerConditionalLogic,
    parentCondition: Condition | undefined,
    interaction: Interaction
  ) {
    return {
      ...baseContext,
      triggerConditionalLogic,
      parentCondition,
      interaction,
    };
  }

  private createTargetTransformContext(
    baseContext: TransformBaseContext<TContext>,
    parent: Trigger | TimelineAction,
    grandparent: Interaction | Timeline
  ) {
    return {
      ...baseContext,
      parent,
      grandparent,
    };
  }

  private transformIX3Data(
    data: Draft<{ix3Interactions?: Interaction[]; ix3Timelines?: Timeline[]}>,
    context: TransformBaseContext<TContext>
  ): void {
    // Transform interactions
    if (data.ix3Interactions) {
      PathTracker.withPath(this.pathTracker, 'ix3Interactions', () => {
        data.ix3Interactions!.forEach((interaction, index) => {
          PathTracker.withPath(this.pathTracker, String(index), () => {
            this.transformInteraction(
              interaction as Draft<Interaction>,
              context
            );
          });
        });
      });
    }

    // Transform timelines
    if (data.ix3Timelines) {
      PathTracker.withPath(this.pathTracker, 'ix3Timelines', () => {
        data.ix3Timelines!.forEach((timeline, index) => {
          PathTracker.withPath(this.pathTracker, String(index), () => {
            this.transformTimeline(timeline as Draft<Timeline>, context);
          });
        });
      });
    }
  }

  private transformInteraction(
    interaction: Draft<Interaction>,
    context: TransformBaseContext<TContext>
  ): void {
    ErrorCollector.safeExecute(
      () => {
        // Apply core transforms
        if (CoreTransforms.isTransformable(interaction)) {
          if (context.environment === 'publish') {
            CoreTransforms.applyCoreTransforms(
              interaction,
              'interaction',
              this.config.coreConfig
            );
            CoreTransforms.stripInteractionFields(interaction);
          }
        }

        // Transform triggers
        if (interaction.triggers) {
          PathTracker.withPath(this.pathTracker, 'triggers', () => {
            interaction.triggers!.forEach((trigger, index) => {
              PathTracker.withPath(this.pathTracker, String(index), () => {
                this.transformTrigger(
                  trigger as Draft<Trigger>,
                  interaction,
                  context
                );
              });
            });
          });
        }
      },
      this.errorCollector,
      this.pathTracker.current()
    );
  }

  private transformTimeline(
    timeline: Draft<Timeline>,
    context: TransformBaseContext<TContext>
  ): void {
    ErrorCollector.safeExecute(
      () => {
        // Apply core transforms
        if (CoreTransforms.isTransformable(timeline)) {
          if (context.environment === 'publish') {
            CoreTransforms.applyCoreTransforms(
              timeline,
              'timeline',
              this.config.coreConfig
            );
            CoreTransforms.stripTimelineFields(timeline);
          }
        }

        // Transform actions
        if (timeline.actions) {
          // Filter out hidden actions first (before transformation strips the field)
          timeline.actions = timeline.actions.filter(
            (action) => !action.hidden
          );

          PathTracker.withPath(this.pathTracker, 'actions', () => {
            timeline.actions!.forEach((action, index) => {
              PathTracker.withPath(this.pathTracker, String(index), () => {
                this.transformTimelineAction(
                  action as Draft<TimelineAction>,
                  timeline,
                  context
                );
              });
            });
          });
        }
      },
      this.errorCollector,
      this.pathTracker.current()
    );
  }

  private transformTrigger(
    trigger: Draft<Trigger>,
    interaction: Draft<Interaction>,
    context: TransformBaseContext<TContext>
  ): void {
    const [extensionKey, config, target] = trigger;

    ErrorCollector.safeExecute(
      () => {
        // Apply core transforms to trigger config
        if (config && CoreTransforms.isTransformable(config)) {
          if (context.environment === 'publish') {
            CoreTransforms.applyCoreTransforms(
              config,
              'trigger',
              this.config.coreConfig
            );
            CoreTransforms.stripTriggerConfigFields(config);
          }
        }

        // Apply plugin transform if available
        const transform = this.triggerTransforms.get(extensionKey);
        if (transform && config) {
          const triggerContext = this.createTriggerTransformContext(
            context,
            trigger as Trigger,
            interaction as Interaction
          );
          const result = transform(
            config,
            triggerContext,
            this.pathTracker.current()
          );
          if (result !== undefined) {
            // Replace the trigger config
            trigger[1] = result as Draft<TriggerConfig>;
          }
        }

        // Transform target if present
        if (target) {
          PathTracker.withPath(this.pathTracker, 'target', () => {
            this.transformTarget(
              target as Draft<TargetDefinition>,
              trigger as Trigger,
              interaction as Interaction,
              context
            );
          });
        }

        // Transform conditions in trigger config
        if (config?.conditionalLogic?.conditions) {
          PathTracker.withPath(
            this.pathTracker,
            'config.conditionalLogic.conditions',
            () => {
              config.conditionalLogic!.conditions.forEach(
                (condition, index) => {
                  PathTracker.withPath(this.pathTracker, String(index), () => {
                    this.transformCondition(
                      condition as Draft<Condition>,
                      config.conditionalLogic!,
                      undefined, // No parent condition for root level
                      interaction,
                      context
                    );
                  });
                }
              );
            }
          );
        }
      },
      this.errorCollector,
      this.pathTracker.current(),
      extensionKey
    );
  }

  private transformTimelineAction(
    action: Draft<TimelineAction>,
    timeline: Draft<Timeline>,
    context: TransformBaseContext<TContext>
  ): void {
    ErrorCollector.safeExecute(
      () => {
        // Apply core transforms
        if (CoreTransforms.isTransformable(action)) {
          if (context.environment === 'publish') {
            CoreTransforms.applyCoreTransforms(
              action,
              'action',
              this.config.coreConfig
            );
            CoreTransforms.stripActionFields(action);
          }
        }

        if (action.timing && CoreTransforms.isTransformable(action.timing)) {
          if (context.environment === 'publish') {
            CoreTransforms.applyCoreTransforms(
              action.timing,
              'timing',
              this.config.coreConfig
            );
          }
        }

        // Transform targets
        if (action.targets) {
          PathTracker.withPath(this.pathTracker, 'targets', () => {
            action.targets!.forEach((target, index) => {
              PathTracker.withPath(this.pathTracker, String(index), () => {
                this.transformTarget(
                  target as Draft<TargetDefinition>,
                  action as TimelineAction,
                  timeline as Timeline,
                  context
                );
              });
            });
          });
        }

        // Transform properties (contains extension keys for actions)
        if (action.properties) {
          PathTracker.withPath(this.pathTracker, 'properties', () => {
            Object.entries(action.properties!).forEach(
              ([extensionKey, properties]) => {
                if (properties && CoreTransforms.isTransformable(properties)) {
                  if (context.environment === 'publish') {
                    CoreTransforms.applyCoreTransforms(
                      properties,
                      'properties',
                      this.config.coreConfig
                    );
                  }
                  if (context.propertiesTransform) {
                    context.propertiesTransform(
                      properties as unknown as Record<string, unknown>
                    );
                  }
                }

                PathTracker.withPath(this.pathTracker, extensionKey, () => {
                  const transform = this.actionTransforms.get(
                    extensionKey as ExtensionKey
                  );
                  if (transform && properties) {
                    const actionContext = this.createActionTransformContext(
                      context,
                      action as TimelineAction,
                      timeline as Timeline
                    );
                    const result = transform(
                      properties,
                      actionContext,
                      this.pathTracker.current()
                    );
                    if (result !== undefined) {
                      // Replace the properties for this extension
                      action.properties![extensionKey as ExtensionKey] =
                        result as Draft<AnimationConfigValues>;
                    }
                  }
                });
              }
            );
          });
        }
      },
      this.errorCollector,
      this.pathTracker.current()
    );
  }

  private transformCondition(
    condition: Draft<Condition>,
    triggerConditionalLogic: TriggerConditionalLogic,
    parentCondition: Condition | undefined,
    interaction: Draft<Interaction>,
    context: TransformBaseContext<TContext>
  ): void {
    const extensionKey = condition[0];
    const conditionValue = condition[1];

    ErrorCollector.safeExecute(
      () => {
        const transform = this.conditionTransforms.get(extensionKey);
        if (transform && conditionValue !== undefined) {
          const conditionContext = this.createConditionTransformContext(
            context,
            triggerConditionalLogic,
            parentCondition,
            interaction as Interaction
          );
          const result = transform(
            condition,
            conditionContext,
            this.pathTracker.current()
          );
          if (result !== undefined) {
            // Replace the condition value
            condition[0] = result[0];
            condition[1] = result[1];
            if (result[2]) condition[2] = result[2];
          }
        }
      },
      this.errorCollector,
      this.pathTracker.current(),
      extensionKey
    );
  }

  private transformTarget(
    target: Draft<TargetDefinition>,
    parent: Trigger | TimelineAction,
    grandparent: Interaction | Timeline,
    context: TransformBaseContext<TContext>
  ): void {
    const [extensionKey, targetValue] = target;

    ErrorCollector.safeExecute(
      () => {
        // Apply extension-specific transform if available
        const transform = this.targetTransforms.get(extensionKey);
        if (transform && targetValue !== undefined) {
          const targetContext = this.createTargetTransformContext(
            context,
            parent,
            grandparent
          );
          const result = transform(
            target,
            targetContext,
            this.pathTracker.current()
          );
          if (result !== undefined) {
            // Replace the target
            target[0] = result[0];
            target[1] = result[1];
          }
        }

        // Transform filter context if present
        if (target.length === 3 && target[2]) {
          PathTracker.withPath(this.pathTracker, 'filterContext', () => {
            const filterContext = target[2] as Draft<any>;
            if (filterContext?.filterBy) {
              const filterExtKey = filterContext.filterBy[0];
              const filterTransform = this.targetTransforms.get(filterExtKey);

              if (filterTransform) {
                PathTracker.withPath(this.pathTracker, 'filterBy', () => {
                  // Create array with REFERENCE to filterBy values for mutation
                  const filterTarget: Draft<TargetDefinition> =
                    filterContext.filterBy as Draft<TargetDefinition>;

                  // Call transform - it may mutate filterTarget[1] directly or return new target
                  const result = filterTransform(
                    filterTarget,
                    this.createTargetTransformContext(
                      context,
                      parent,
                      grandparent
                    ),
                    this.pathTracker.current()
                  );

                  // If transform returns new target, replace filterBy
                  // Otherwise, mutations to filterTarget are already reflected in filterContext.filterBy
                  if (result) {
                    filterContext.filterBy = [result[0], result[1]];
                  }
                });
              }
            }

            if (
              context.environment === 'publish' &&
              filterContext?.relationship === 'none'
            ) {
              filterContext.filterBy = undefined;
            }
          });
        }
      },
      this.errorCollector,
      this.pathTracker.current(),
      extensionKey
    );
  }
}

class PathTracker {
  static withPath<T>(
    pathTracker: PathTracker,
    segment: string,
    operation: () => T
  ): T {
    pathTracker.push(segment);
    try {
      return operation();
    } finally {
      pathTracker.pop();
    }
  }

  private path: string[] = [];

  push(segment: string): void {
    this.path.push(segment);
  }

  pop(): void {
    this.path.pop();
  }

  current(): string {
    return this.path.join('.');
  }

  reset(): void {
    this.path.length = 0;
  }

  clone(): PathTracker {
    const tracker = new PathTracker();
    tracker.path = [...this.path];
    return tracker;
  }
}

export interface TransformError {
  path: string;
  extensionKey?: ExtensionKey;
  message: string;
  severity: 'error' | 'warning';
}

class ErrorCollector {
  static safeExecute<T>(
    operation: () => T,
    errorCollector: ErrorCollector,
    path: string,
    extensionKey?: ExtensionKey
  ): T | undefined {
    try {
      return operation();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      errorCollector.addError(
        path,
        `Transform failed: ${message}`,
        extensionKey
      );
      return undefined;
    }
  }
  private errors: TransformError[] = [];

  add(error: TransformError): void {
    this.errors.push(error);
  }

  addError(
    path: string,
    message: string,
    extensionKey?: ExtensionKey,
    severity: 'error' | 'warning' = 'error'
  ): void {
    this.add({
      path,
      message,
      extensionKey,
      severity,
    });
  }

  getErrors(): TransformError[] {
    return [...this.errors];
  }

  hasErrors(): boolean {
    return this.errors.length > 0;
  }

  clear(): void {
    this.errors.length = 0;
  }

  merge(other: ErrorCollector): void {
    this.errors.push(...other.getErrors());
  }
}

export interface TransformEngineConfig {
  coreConfig: CoreTransformConfig;
  enableErrorCollection: boolean;
  enablePathTracking: boolean;
}

export interface CoreTransformConfig {
  stripFields: string[];
  defaults: Record<string, Record<string, any>>;
  optimizations: {
    removeEmptyArrays: boolean;
    removeNullValues: boolean;
    removeEmptyObjects: boolean;
    /** Keys that must never be stripped, even when empty/null (e.g. required structural fields). */
    preserveKeys?: string[];
  };
}

class CoreTransforms {
  /**
   * Strips server-specific fields from any object
   * @param node - The object to strip fields from
   * @param config - Configuration specifying which fields to strip
   */
  static stripServerFields(node: any, config: CoreTransformConfig): void {
    if (!node || typeof node !== 'object') return;

    config.stripFields.forEach((field) => {
      if (field in node) {
        delete node[field]; // eslint-disable-line webflow/no-delete
      }
    });
  }

  /**
   * Strips TimelineAction fields from any object
   * @param node - The object to strip fields from
   * @param config - Configuration specifying which fields to strip
   */
  static stripActionFields(action: Draft<Partial<TimelineAction>>): void {
    if (!action || typeof action !== 'object') return;

    delete action.presetId; // eslint-disable-line webflow/no-delete
    delete action.color; // eslint-disable-line webflow/no-delete
    delete action.name; // eslint-disable-line webflow/no-delete
    delete action.hidden; // eslint-disable-line webflow/no-delete
  }

  /**
   * Strips Interaction fields from any object
   * @param node - The object to strip fields from
   * @param config - Configuration specifying which fields to strip
   */
  static stripInteractionFields(
    interaction: Draft<Partial<Interaction>>
  ): void {
    if (!interaction || typeof interaction !== 'object') return;

    delete interaction.name; // eslint-disable-line webflow/no-delete
    // The Designer's settings baseline for a component interaction. Nothing in
    // the runtime reads it, and it carries component/element ids plus a bounded
    // block of context per instance — so publishing it would put Designer-only
    // state, and its weight, into every published page and DevLink payload.
    delete interaction.componentSeed; // eslint-disable-line webflow/no-delete
  }

  /**
   * Strips TriggerConfig fields from any object
   * @param config
   */
  static stripTriggerConfigFields(
    config: Draft<Partial<TriggerConfig<unknown>>>
  ): void {
    if (!config || typeof config !== 'object') return;
    if (
      config.controlType === TimelineControlType.SCROLL &&
      config.scrollTriggerConfig
    ) {
      config.scrollTriggerConfig.showMarkers = undefined;
      // Strip values that match the runtime default so they don't ride along in
      // the published payload. `pinSpacing: true` is GSAP's own default.
      if (config.scrollTriggerConfig.pinSpacing === true) {
        config.scrollTriggerConfig.pinSpacing = undefined;
      }
    }
  }

  /**
   * Strips Timeline fields from any object
   * @param node - The object to strip fields from
   * @param config - Configuration specifying which fields to strip
   */
  static stripTimelineFields(timeline: Draft<Partial<Timeline>>): void {
    if (!timeline || typeof timeline !== 'object') return;

    // Designer-only fields: the runtime routes grouped timelines by `groupId`
    // and never reads the human-friendly group name or the action-preset
    // governance ids, so strip them from published output. Mirrors
    // stripInteractionFields dropping `interaction.name` and stripActionFields
    // dropping action `name`/`presetId`. `groupId` is intentionally preserved.
    delete timeline.name; // eslint-disable-line webflow/no-delete
    delete timeline.actionPresetIds; // eslint-disable-line webflow/no-delete
  }

  /**
   * Removes properties that match default values
   * @param node - The object to process
   * @param nodeType - Type of node to get defaults for
   * @param config - Configuration containing default values
   */
  static removeDefaults(
    node: any,
    nodeType: string,
    config: CoreTransformConfig
  ): void {
    if (!node || typeof node !== 'object') return;

    const defaults = config.defaults[nodeType];
    if (!defaults) return;

    Object.entries(defaults).forEach(([key, defaultValue]) => {
      if (key in node && node[key] === defaultValue) {
        delete node[key]; // eslint-disable-line webflow/no-delete
      }
    });
  }

  /**
   * Optimizes object structure by removing empty arrays, null values, and empty objects
   * @param node - The object to optimize
   * @param config - Configuration specifying optimization rules
   */
  static optimizeStructure(node: any, config: CoreTransformConfig): void {
    if (!node || typeof node !== 'object') return;

    Object.keys(node).forEach((key) => {
      const value = node[key];

      // Never strip preserved keys — required structural fields the runtime
      // reads unconditionally (e.g. timelineIds) must survive even when empty.
      if (config.optimizations.preserveKeys?.includes(key)) {
        return;
      }

      // Remove empty arrays
      if (
        config.optimizations.removeEmptyArrays &&
        Array.isArray(value) &&
        value.length === 0
      ) {
        delete node[key]; // eslint-disable-line webflow/no-delete
        return;
      }

      // Remove null values
      if (config.optimizations.removeNullValues && value == null) {
        delete node[key]; // eslint-disable-line webflow/no-delete
        return;
      }

      // Remove empty objects
      if (
        config.optimizations.removeEmptyObjects &&
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.keys(value).length === 0
      ) {
        delete node[key]; // eslint-disable-line webflow/no-delete
        return;
      }
    });
  }

  /**
   * Applies all core transforms to a node
   * @param node - The object to transform
   * @param nodeType - Type of node for default removal
   * @param config - Core transform configuration
   */
  static applyCoreTransforms(
    node: any,
    nodeType: string,
    config: CoreTransformConfig
  ): void {
    CoreTransforms.stripServerFields(node, config);
    CoreTransforms.removeDefaults(node, nodeType, config);
    CoreTransforms.optimizeStructure(node, config);
  }

  /**
   * Validates that a node is safe to transform
   * @param node - The node to validate
   * @returns true if the node can be safely transformed
   */
  static isTransformable(node: any): boolean {
    return node != null && typeof node === 'object';
  }
}
